import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { DevModeContext } from '../App';
import { FaSignOutAlt, FaUser, FaCog, FaBars, FaTimes, 
  FaWallet, FaSync, FaChartBar, FaFileAlt, FaChartLine, FaEllipsisV, FaBell, FaVideo, FaCrown, FaChartPie, FaQuestionCircle, FaStore } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { getDoc, doc, collection, query, orderBy, limit, onSnapshot, updateDoc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import ConfirmationModal from './ConfirmationModal';
import UpgradeToAccessModal from './UpgradeToAccessModal';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();
  const isDevMode = useContext(DevModeContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [realUserIsAdmin, setRealUserIsAdmin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  const menuItems = [
    { title: 'Expenses', icon: <FaWallet />, path: '/expense' },
    { title: 'Recurring', icon: <FaSync />, path: '/recurring' },
    { title: 'Budgets', icon: <FaChartBar />, path: '/budgets' },
    { title: 'Income Tracking', icon: <FaChartLine />, path: '/income' },
    { title: 'Statement', icon: <FaFileAlt />, path: '/statement' },
    { title: 'Tutorial', icon: <FaVideo />, path: '/tutorial' },
  ];

  const premiumMenuItems = [
    { title: 'IntelliSpend', icon: <FaChartPie />, path: '/intelliSpend', isPremium: true },
    { title: 'Community Forum', icon: <FaUser />, path: '/community', isPremium: true },
  ];

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      document.documentElement.className = '';
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Failed to log out');
    }
    setShowLogoutModal(false);
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    toast.success('Stopped impersonating. You are now back to your admin account.');
    navigate('/admin');
  };

  const dropdownItems = [
    { title: 'Settings', icon: <FaCog />, onClick: () => navigate('/settings') },
    { title: 'Profile', icon: <FaUser />, onClick: () => navigate('/profile') },
    { title: 'Help', icon: <FaQuestionCircle />, onClick: () => navigate('/help') },
    { 
      title: 'Sign Out', 
      icon: <FaSignOutAlt />, 
      onClick: isImpersonating ? null : handleLogoutClick,
      disabled: isImpersonating 
    },
  ];

  useEffect(() => {
    let unsubscribe = null;

    const setupUserListener = async () => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        
        unsubscribe = onSnapshot(userRef, (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            const userIsAdmin = userData.isAdmin || false;
            setRealUserIsAdmin(userIsAdmin);
            
            // When impersonating, admin privileges are disabled
            setIsAdmin(isImpersonating ? false : userIsAdmin);
            setIsPremium(userData.membershipLevel === 'premium' || userData.membershipLevel === 'promo');
          }
        }, (error) => {
          console.error('Error in user listener:', error);
        });
      }
    };

    setupUserListener();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, isImpersonating]);

  useEffect(() => {
    const effectiveUserId = isImpersonating ? impersonatedUser?.id : currentUser?.uid;
    
    if (effectiveUserId) {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', effectiveUserId),
        orderBy('createdAt', 'desc'),
        limit(5)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() // Ensure date conversion
        }));
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
      }, (error) => {
        console.error('Error in notifications listener:', error);
      });

      return () => unsubscribe();
    }
  }, [currentUser, isImpersonating, impersonatedUser]);

  const handleNotificationClick = async (notificationId) => {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true
    });
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      for (const notification of unreadNotifications) {
        const notificationRef = doc(db, 'notifications', notification.id);
        await updateDoc(notificationRef, {
          read: true
        });
      }
      // Update local state immediately
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const menuVariants = {
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    },
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    let date;
    
    // Convert to Date object
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    // Get today's date
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Format time
    const time = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });

    // Check if it's today, yesterday, or older
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${time}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${time}`;
    } else {
      return `${date.toLocaleDateString()} at ${time}`;
    }
  };

  const NotificationButton = () => (
    <div className="relative">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="p-2 text-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none relative"
      >
        <FaBell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {showNotifications && (
          <>
            {/* Overlay for clicking outside */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                markAllNotificationsAsRead();
                setShowNotifications(false);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2 }}
              className="fixed left-4 right-4 sm:absolute sm:left-auto sm:right-0 mt-2 w-auto sm:w-96 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-2">
                <div className="px-4 py-3 border-b dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Notifications
                    </h3>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
                          {unreadCount} new
                        </span>
                      )}
                      <button
                        onClick={() => {
                          markAllNotificationsAsRead();
                          setShowNotifications(false);
                        }}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                      >
                        <FaTimes className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length > 0 ? (
                    <>
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification.id)}
                          className={`px-4 py-3 border-b dark:border-gray-700 last:border-0 cursor-pointer transition-colors duration-150 
                            ${!notification.read 
                              ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read 
                                ? 'text-gray-900 dark:text-white font-medium' 
                                : 'text-gray-600 dark:text-gray-300'}`}>
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {formatDate(notification.createdAt)}
                              </p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              notification.read 
                                ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {notification.read ? 'Read' : 'New'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                      <p>No notifications</p>
                    </div>
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="px-4 py-3 border-t dark:border-gray-700">
                    <button
                      onClick={() => {
                        markAllNotificationsAsRead();
                        navigate('/notifications');
                        setShowNotifications(false);
                      }}
                      className="w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-sm z-50 transform-gpu will-change-transform min-h-[4rem] sm:min-h-[5rem] select-none">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-1 md-space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
              >
                <FaBars className="h-6 w-6" />
              </button>
              <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img
                  src="/cashsule_new.png"
                  alt="Cashsule Icon"
                  className="h-8 w-auto sm:h-14"
                />
                <div className="flex flex-col">
                  <h1 className="ml-2 sm:ml-4 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    Cashsule
                  </h1>
                  {isDevMode && (
                    <div className="ml-2 sm:ml-4 text-xs font-medium px-2 py-0.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 rounded-md inline-flex items-center">
                      DEV MODE
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="hidden md:inline text-gray-700">
                Welcome, {isImpersonating ? impersonatedUser?.username : (currentUser?.displayName || currentUser?.email)}
                {isImpersonating && (
                  <span className="ml-2 px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                    Simulating
                  </span>
                )}
              </span>
              {realUserIsAdmin && isImpersonating && (
                <button
                  onClick={handleStopImpersonation}
                  className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  <span className="hidden sm:inline">Stop Simulating</span>
                  <span className="sm:hidden">Stop Sim</span>
                </button>
              )}
              {isAdmin && !isImpersonating && (
                <button
                  onClick={() => navigate('/admin')}
                  className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="hidden sm:inline">Admin Dashboard</span>
                  <span className="sm:hidden">Admin</span>
                </button>
              )}
              
              {/* Desktop buttons */}
              <div className="hidden sm:flex items-center space-x-2">
                <NotificationButton />
                <button
                  onClick={() => navigate('/settings')}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                >
                  <FaCog className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                >
                  <FaUser className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/help')}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                >
                  <FaQuestionCircle className="h-5 w-5" />
                </button>
                <button
                  onClick={handleLogoutClick}
                  disabled={isImpersonating}
                  className={`p-2 focus:outline-none ${
                    isImpersonating 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-gray-600 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <FaSignOutAlt className="h-5 w-5" />
                </button>
              </div>

              {/* Mobile dropdown */}
              <div className="sm:hidden flex items-center">
                <NotificationButton />
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="p-2 text-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                  >
                    <FaEllipsisV className="h-5 w-5" />
                  </button>

                  <AnimatePresence>
                    {isDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsDropdownOpen(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -20 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                        >
                          <div className="py-1">
                            {dropdownItems.map((item) => (
                              <button
                                key={item.title}
                                onClick={() => {
                                  if (!item.disabled && item.onClick) {
                                    item.onClick();
                                    setIsDropdownOpen(false);
                                  }
                                }}
                                disabled={item.disabled}
                                className={`w-full flex items-center px-4 py-2 text-sm ${
                                  item.disabled 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                <span className="mr-3">{item.icon}</span>
                                {item.title}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay - update z-index to be below header */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
            className="fixed inset-0 bg-black bg-opacity-50 z-30"
          />
        )}
      </AnimatePresence>

      {/* Sidebar Menu - update z-index to be below header */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-gray-900 shadow-lg z-40"
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <div className="flex items-center">
                <img
                  src="/cashsule_new.png"
                  alt="Cashsule Icon"
                  className="h-8 w-auto"
                />
                <h1 className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                  Cashsule
                </h1>
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex flex-col h-full pt-2">
              {/* Regular Menu Items */}
              <div className="px-4 py-2">
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Main Menu
                </h2>
              </div>
              {menuItems.map((item) => (
                <motion.button
                  key={item.path}
                  onClick={() => {
                    if (!item.disabled) {
                      navigate(item.path);
                      setIsMenuOpen(false);
                    }
                  }}
                  className={`flex items-center space-x-4 px-6 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200
                    ${location.pathname === item.path 
                      ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400' 
                      : 'text-gray-700 dark:text-gray-300'}
                    ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  whileHover={!item.disabled ? { x: 10 } : {}}
                  whileTap={!item.disabled ? { scale: 0.95 } : {}}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.title}</span>
                </motion.button>
              ))}

              {/* Premium Menu Items */}
              <div className="px-4 py-2 mt-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Premium Features
                  </h2>
                  {!isPremium && !isAdmin && (
                    <div className="flex items-center space-x-1 text-yellow-500">
                      <FaCrown className="h-3 w-3" />
                      <span className="text-xs font-medium">Premium</span>
                    </div>
                  )}
                </div>
              </div>
              {premiumMenuItems.map((item) => (
                <motion.button
                  key={item.path}
                  onClick={() => {
                    if (!item.disabled) {
                      if (!isPremium && !isAdmin) {
                        setShowUpgradeModal(true);
                        setIsMenuOpen(false);
                      } else {
                        navigate(item.path);
                        setIsMenuOpen(false);
                      }
                    }
                  }}
                  className={`flex items-center space-x-4 px-6 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200
                    ${location.pathname === item.path 
                      ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400' 
                      : 'text-gray-700 dark:text-gray-300'}
                    ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  whileHover={!item.disabled ? { x: 10 } : {}}
                  whileTap={!item.disabled ? { scale: 0.95 } : {}}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.title}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update spacer div with min-height */}
      <div className="min-h-[4rem] sm:min-h-[5rem] w-full flex-none" />

      {/* Add ConfirmationModal at the end of the component */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
      />

      {/* Add UpgradeToAccessModal */}
      <UpgradeToAccessModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </>
  );
};

export default Header; 